import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface FavoritesContextType {
  favorites: {
    sequences: string[];
    displays: string[];
  };
  toggleSequenceFavorite: (sequenceId: string) => void;
  toggleDisplayFavorite: (displayId: string) => void;
  isSequenceFavorite: (sequenceId: string) => boolean;
  isDisplayFavorite: (displayId: string) => boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<{ sequences: string[]; displays: string[] }>({
    sequences: [],
    displays: []
  });
  const { user, isAuthenticated } = useAuth();

  // Load favorites from localStorage when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      const storedFavorites = localStorage.getItem(`favorites_${user.id}`);
      if (storedFavorites) {
        setFavorites(JSON.parse(storedFavorites));
      }
    } else {
      // Clear favorites when user logs out
      setFavorites({ sequences: [], displays: [] });
    }
  }, [user, isAuthenticated]);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    if (isAuthenticated && user) {
      localStorage.setItem(`favorites_${user.id}`, JSON.stringify(favorites));
    }
  }, [favorites, user, isAuthenticated]);

  const toggleSequenceFavorite = (sequenceId: string) => {
    if (!isAuthenticated) {
      // Instead of blocking, we'll dispatch an event that the app can listen to
      const authRequiredEvent = new CustomEvent('auth-required', {
        detail: {
          action: 'favorite',
          sequenceId
        }
      });
      window.dispatchEvent(authRequiredEvent);
      return;
    }
    
    setFavorites(prev => {
      const newSequences = prev.sequences.includes(sequenceId)
        ? prev.sequences.filter(id => id !== sequenceId)
        : [...prev.sequences, sequenceId];
      
      return {
        ...prev,
        sequences: newSequences
      };
    });
  };

  const toggleDisplayFavorite = (displayId: string) => {
    if (!isAuthenticated) return;
    
    setFavorites(prev => {
      const newDisplays = prev.displays.includes(displayId)
        ? prev.displays.filter(id => id !== displayId)
        : [...prev.displays, displayId];
      
      return {
        ...prev,
        displays: newDisplays
      };
    });
  };

  const isSequenceFavorite = (sequenceId: string) => favorites.sequences.includes(sequenceId);
  const isDisplayFavorite = (displayId: string) => favorites.displays.includes(displayId);

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        toggleSequenceFavorite,
        toggleDisplayFavorite,
        isSequenceFavorite,
        isDisplayFavorite
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}; 